import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { config } from '@app/core/app-config';
import { BillingFacade } from '@app/shared/+state/billing';
import { LoadingState } from '@app/shared/interfaces';
import { IntercomService } from '@app/shared/services/intercom.service';
import { combineLatest } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';

const CHURNKEY_SCRIPT_URL = 'https://assets.churnkey.co/js/app.js';
const MODE = config.CHURNKEY_MODE;

interface ChurnkeyInfoPayload {
  chargebee_customer_id: string;
  chargebee_subscription_id: string;
  churnkey_auth_hash: string;
}

interface ChurnkeyOptions {
  subscriptionId: string;
  customerId: string;
  authHash: string;
  appId: typeof config.CHURNKEY_APP_ID;
  mode: 'live' | 'test';
  provider: 'chargebee';
  record: boolean;
  // handler callbacks
  handleSupportRequest?: ChurnkeyHandleSupportRequestFunction;
  handleCancel?: ChurnkeyHandleCancelFunction;
  // listener callbacks
  onCancel?: ChurnkeyOnCancelFunction;
  onClose?: ChurnkeyOnCloseFunction;
  onPause?: () => void;
  onTrialExtension?: () => void;
  onDiscount?: () => void;
  onPlanChange?: () => void;
  onGoToAccount?: () => void;
  onStepChange?: ChurnkeyOnStepChangeFunction;
}

interface ChurnkeyStep {
  stepType: string;
  header: string;
  description: string; // can contain html
  guid: string;
  offer: {
    guid: string;
    header: string;
    description: string;
    offerType: string;
  };
}
type ChurnkeyHandleCancelFunction = (
  customer: ChurnkeyCustomer,
  surveyResponse: string,
  freeformFeedback: string
) => Promise<void | { message: string }>;
type ChurnkeyHandleSupportRequestFunction = () => void;
type ChurnkeyOnCancelFunction = () => void;
type ChurnkeyOnCloseFunction = () => void;
type ChurnkeyOnStepChangeFunction = (newStep: ChurnkeyStep, oldStep: ChurnkeyStep) => void;

// There's a lot of attributes here, but we aren't using them right now
interface ChurnkeyCustomer {
  first_name: string;
  email: string;
}

interface Churnkey {
  created: boolean;
  initialized?: boolean;
  init?: (action: string, options: ChurnkeyOptions) => void;
  hide?: () => void;
  clearState?: () => void;
}

/**
 * Service to interact with Churnkey, to manage cancellations and trial extensions: https://app.churnkey.co/
 *
 * Local development:
 * 1. Add the following to your environment.ts file:
 * ```
 * churnkey: {
    appId: 'GET_THIS_FROM_IN_CHURNKEY',
    mode: 'test'
  }
  * ```
  * 2. Add the following to your Smartbnb .env file:
  * ```
  * CHURNKEY_API_KEY=test_GET_THIS_FROM_IN_CHURNKEY
  * ```
  * 3. Make sure your user has a subscription in the test chargebee account.
 */
@Injectable({
  providedIn: 'root',
})
export class ChurnkeyService {
  private reportMissingFeatures = false;
  constructor(
    @Inject('apiUrl') private apiUrl: string,
    @Inject(DOCUMENT)
    private _document: Document & {
      defaultView: Window & {
        churnkey: Churnkey;
      };
    },
    private readonly http: HttpClient,
    private readonly intercomService: IntercomService,
    private readonly billingFacade: BillingFacade,
    private readonly router: Router
  ) {
    this.loadScript();
  }

  public show() {
    combineLatest([
      this.loadChurnkeyInfo().pipe(
        tap((info) => {
          if (!info) {
            throw new Error('Unable to load Churnkey billing info');
          }
        }),
        filter((info) => Boolean(info))
      ),
      this.billingFacade.subscription$,
    ])
      .pipe(take(1))
      .subscribe(([info, subscription]) => {
        const cancelImmediately = subscription.status === 'trialing' || subscription.status === 'pending';
        const handleCancel = cancelImmediately ? this.handleCancel : undefined;
        const {
          chargebee_customer_id: customerId,
          chargebee_subscription_id: subscriptionId,
          churnkey_auth_hash: authHash,
        } = info;
        this.churnkeyInstance?.init('show', {
          subscriptionId,
          customerId,
          authHash,
          appId: config.CHURNKEY_APP_ID,
          mode: MODE,
          provider: 'chargebee',
          record: true,
          handleCancel,
          handleSupportRequest: this.handleSupportRequest,
          onGoToAccount: this.refreshState,
          onClose: this.refreshState,
          onStepChange: this.onStepChange,
        });
      });
  }

  private loadScript() {
    if (!this.window.churnkey || !this.window.churnkey.created) {
      const churnkeyScript = this._document.createElement('script');
      churnkeyScript.type = 'text/javascript';
      churnkeyScript.async = true;
      churnkeyScript.src = `${CHURNKEY_SCRIPT_URL}?appId=${config.CHURNKEY_APP_ID}`;
      document.body.appendChild(churnkeyScript);
      this.window.churnkey = { created: true };
    }
  }

  private get churnkeyInstance() {
    if (!this.window.churnkey.created) {
      throw new Error('Tried to access Churnkey before it was created.');
    }

    if (!this.window.churnkey.init) {
      throw new Error('Tried to access Churnkey before it was ready.');
    }

    return this.window.churnkey;
  }

  private get window() {
    return this._document.defaultView;
  }

  private loadChurnkeyInfo() {
    return this.http.get<ChurnkeyInfoPayload>(`${this.apiUrl}/settings/billing/subscription/cancel`);
  }

  // Defined as an arrow function to preserve `this` context and allow for easier typing
  protected handleSupportRequest: ChurnkeyHandleSupportRequestFunction = () => {
    this.churnkeyInstance.hide();
  };

  /**
   * This cancellation is effective immediately, unlike the cancellation
   * of an active customer which will end at the end of the billing period.
   * Churnkey handles the cancellation of active customers directly
   *
   * Defined as an arrow function to preserve `this` context and allow for easier typing
   */
  protected handleCancel: ChurnkeyHandleCancelFunction = (customer, surveyResponse, freeformFeedback) => {
    this.billingFacade.cancelSubscription({ reason: surveyResponse, textReason: freeformFeedback });
    return this.billingFacade.cancelSubscriptionSubmittingState$
      .pipe(
        filter((state) => state !== LoadingState.NotSent && state !== LoadingState.Pending),
        tap((state) => {
          if (state === LoadingState.Error) {
            this.intercomService.showNewMessage('Failed to cancel subscription.');
          }
        }),
        map((state) => {
          if (state === LoadingState.Error) {
            throw {
              message: 'Failed to cancel subscription. Please reach out to support.',
            };
          }
          return;
        }),
        take(1)
      )
      .toPromise();
  };

  // Defined as arrow function to preserve `this` context
  protected refreshState = () => {
    this.window.location.reload();
  };

  protected onStepChange: ChurnkeyOnStepChangeFunction = (newStep) => {
    const isContactStep = newStep.offer?.offerType === 'CONTACT';
    const isMainSupportStep = newStep.offer?.header?.toLowerCase()?.includes('here to help');

    // These query params are used to combine with a button click target in
    // Intercom to trigger a specific workflow. Don't change these without
    // updating Intercom.
    this.router.navigate(['/settings/your-plan'], {
      queryParams: this.buildQueryParams(isContactStep, isMainSupportStep),
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    this.reportMissingFeatures = isContactStep && !isMainSupportStep;
  };

  private buildQueryParams(isContactStep: boolean, isMainSupportStep: boolean) {
    if (!isContactStep) {
      return {
        contact: undefined,
        report_missing_features: undefined,
      };
    }
    if (isMainSupportStep) {
      return {
        contact: true,
        report_missing_features: undefined,
      };
    }
    return {
      contact: undefined,
      report_missing_features: true,
    };
  }
}
